'use client';

import React, { useState, useMemo } from 'react';
import CategoryCard from '@/components/CategoryCard';

import { Grid3X3, List } from 'lucide-react';
import type { Category } from '@/data/categories';

interface AllCategoriesViewProps {
  categories: Category[];
}

export default function AllCategoriesView({ categories }: AllCategoriesViewProps) {
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const filtered = categories;

  const grouped = useMemo(() => {
    return filtered.reduce<Record<string, Category[]>>((acc, cat) => {
      if (!acc[cat.group]) acc[cat.group] = [];
      acc[cat.group].push(cat);
      return acc;
    }, {});
  }, [filtered]);

  const totalPrompts = filtered.reduce((sum, c) => sum + c.promptCount, 0);

  return (
    <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 py-10">
      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-3xl sm:text-4xl font-extrabold text-foreground mb-3">
          All AI Image Prompt Categories
        </h1>
        <p className="text-muted-foreground text-base max-w-2xl">
          Browse {categories.length} categories containing {totalPrompts.toLocaleString()}+ curated AI image prompts for Midjourney, DALL·E 3, and Stable Diffusion.
        </p>
      </div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row gap-4 mb-8 sticky top-16 bg-white/90 backdrop-blur-sm py-4 z-30 border-b border-border -mx-4 sm:-mx-6 lg:-mx-8 xl:-mx-10 px-4 sm:px-6 lg:px-8 xl:px-10">
        <div className="flex items-center gap-2 flex-1">
          <span className="text-xs text-muted-foreground">
            {filtered.length} categories
          </span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 rounded-lg transition-colors ${viewMode === 'grid' ? 'bg-accent text-primary' : 'hover:bg-muted text-muted-foreground'}`}
            aria-label="Grid view"
          >
            <Grid3X3 size={16} />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 rounded-lg transition-colors ${viewMode === 'list' ? 'bg-accent text-primary' : 'hover:bg-muted text-muted-foreground'}`}
            aria-label="List view"
          >
            <List size={16} />
          </button>
        </div>
      </div>

      {/* Categories */}
      {Object.entries(grouped).map(([group, cats]) => (
        <div key={`group-section-${group}`} className="mb-12">
          <h2 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
            {group}
            <span className="text-sm font-normal text-muted-foreground">({cats.length})</span>
          </h2>
          {viewMode === 'grid' ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-4 gap-4">
              {cats.map((cat) => (
                <CategoryCard key={`all-cat-${cat.slug}`} category={cat} variant="default" />
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {cats.map((cat) => (
                <CategoryCard key={`all-cat-list-${cat.slug}`} category={cat} variant="compact" />
              ))}
            </div>
          )}
        </div>
      ))}

      {filtered.length === 0 && (
        <div className="text-center py-16">
          <p className="text-muted-foreground text-lg">No categories found in this group.</p>
        </div>
      )}
    </div>
  );
}